import React, { useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { Modal as SemanticModal, Input } from "semantic-ui-react";

import { isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";
import BasePadding from "@cayuse/shared-react.components.base-padding";
import Button from "@cayuse/shared-react.components.button";
import ButtonGroup from "@cayuse/shared-react.components.button-group";
import IconButton from "@cayuse/shared-react.components.icon-button";
import VerticalSpacing from "@cayuse/shared-react.components.vertical-spacing";
import Modal from "@cayuse/shared-react.components.modal";
import Toggle from "@cayuse/shared-react.components.form.inputs.toggle";
import Table from "components/Table";
import { useDictionary } from "hooks";
import { validateTitle } from "utils";
import { addProjectAttachmentType, toggleProjectAttachmentTypeActive } from "store/actions/tenant";
import { tenantSelectors } from "store/selectors";

import styles from "./styles.css";

const ProjectAttachmentTypesSection = () => {
    const dictionary = useDictionary("attachmentTypes");

    const { projectAttachmentTypes } = useSelector(tenantSelectors.tenant);

    const [ isLoading, setIsLoading ] = useState(false);
    const [ doesTitleExist, setDoesTitleExist ] = useState(false);
    const [ isTitleUnique, setIsTitleUnique ] = useState(true);
    const [ addAttachmentTypeModalOpen, setAddAttachmentTypeModalOpen ] = useState(false);
    const [ attachmentTypeValue, setAttachmentTypeValue ] = useState("");

    const handleOpen = () => {
        setAddAttachmentTypeModalOpen(true);
    };

    const handleClose = () => {
        setIsTitleUnique(true);
        setDoesTitleExist(false);
        setAttachmentTypeValue("");
        setAddAttachmentTypeModalOpen(false);
    };

    const validate = useCallback((title) => {
        const { trimmedTitle, isTitleNotEmpty, isSameTitleNotPresent } = validateTitle(title, projectAttachmentTypes);
        setDoesTitleExist(isTitleNotEmpty);

        if (isTitleNotEmpty) {
            setIsTitleUnique(isSameTitleNotPresent);

            if (isSameTitleNotPresent) {
                setAttachmentTypeValue(trimmedTitle);
            }
        }
    }, [ projectAttachmentTypes ]);

    const handleSave = useCallback(() => {
        if (!isNonEmptyString(attachmentTypeValue)) {
            return;
        }
        setIsLoading(true);
        const newAttachmentType = {
            value: attachmentTypeValue,
            active: true,
        };
        addProjectAttachmentType(newAttachmentType, "projectAttachmentTypes")
            .finally(() => {
                setIsLoading(false);
                handleClose();
            });
    }, [ attachmentTypeValue ]);

    const handleCancel = useCallback(() => {
        setIsTitleUnique(true);
        setDoesTitleExist(false);
        handleClose();
    }, []);

    const attachmentTypeRows = useMemo(() => {
        const isOneAttachmentTypeActive = Array.isArray(projectAttachmentTypes)
            ? projectAttachmentTypes.filter(item => item.active).length === 1
            : false;
        const clickHandler = attachmentType => {
            if (!(isOneAttachmentTypeActive && attachmentType.active)) {
                toggleProjectAttachmentTypeActive(attachmentType.value, "projectAttachmentTypes");
            }
        };

        return Array.isArray(projectAttachmentTypes)
            ? projectAttachmentTypes
                .map(attachmentType => (
                    <Table.Row key={attachmentType.value}>
                        <Table.Cell>{attachmentType.value}</Table.Cell>
                        <Table.Cell>
                            <Toggle
                                active={attachmentType.active}
                                dictionary={dictionary.components.toggle}
                                disabled={isOneAttachmentTypeActive && attachmentType.active}
                                onClick={() => clickHandler(attachmentType)}
                            />
                        </Table.Cell>
                    </Table.Row>
                ))
            : [];
    }, [ projectAttachmentTypes, dictionary ]);

    const addNewAttachmentTypeButton = (
        <IconButton
            icon="plus"
            labelPosition="left"
            primary
            onClick={handleOpen}
        >
            {dictionary.addAttachmentType || "Add New Attachment Type"}
        </ IconButton>
    );

    // TODO: Revisit this use of useMemo, it may not be helpful with so many dependencies
    const addAttachmentTypeModal = useMemo(() => (
        <Modal closeIcon
            trigger={addNewAttachmentTypeButton}
            closeOnEscape={true}
            onClose={handleClose}
            open={addAttachmentTypeModalOpen}
            ariaLabel={dictionary.modalHeader || "Create a new attachment type."}
        >
            <SemanticModal.Header className="whiteOnBlue">
                <h2>{dictionary.modalHeader || "Create a new attachment type."}</h2>
            </SemanticModal.Header>
            <SemanticModal.Content>
                {
                    dictionary.modalContent ||
                    `Enter a title for your new attachment type and click "Save", 
                    your new attachment type will be added to the list and set to active.`
                }
                <VerticalSpacing />{dictionary.modalGuidance || `Click "cancel" to discard changes and return to list`}
                <VerticalSpacing />
                <Table captionText="Project Attachment Types">
                    <Table.Row>
                        <Table.Cell>
                            <Input
                                type="text"
                                autoFocus
                                placeholder={dictionary.inputPlaceholder || `Attachment Type...`}
                                onChange={({ target: { value } }) => validate(value)}
                            />
                        </Table.Cell>
                        <Table.Cell>
                            <span className={styles.validationMessage} hidden={doesTitleExist}>
                                {dictionary.errorNoTitle || "Title must not be empty or all whitespace."}
                            </span>
                            <span className={styles.validationMessage} hidden={isTitleUnique}>
                                {dictionary.errorDuplicateTitle || "Title must be unique."}
                            </span>
                        </Table.Cell>
                    </Table.Row>
                </Table>
                <VerticalSpacing />
            </SemanticModal.Content>
            <SemanticModal.Actions>
                <ButtonGroup>
                    <Button secondary onClick={handleCancel}>
                        {dictionary.cancel || "Cancel"}
                    </Button>

                    <Button
                        primary
                        onClick={handleSave}
                        disabled={!doesTitleExist || !isTitleUnique || isLoading}
                        busy={isLoading}
                        loadingMessage="Save in process"
                    >
                        {dictionary.saveAttachmentType || "Save"}
                    </Button>
                </ButtonGroup>
            </SemanticModal.Actions>
        </Modal>
    ), [
        addNewAttachmentTypeButton,
        addAttachmentTypeModalOpen,
        dictionary.modalHeader,
        dictionary.modalContent,
        dictionary.modalGuidance,
        dictionary.inputPlaceholder,
        dictionary.errorNoTitle,
        dictionary.errorDuplicateTitle,
        dictionary.cancel,
        dictionary.saveAttachmentType,
        doesTitleExist,
        isTitleUnique,
        handleCancel,
        handleSave,
        validate,
        isLoading
    ]);

    return (
        <VerticalSpacing>
            <BasePadding>
                <Table captionText="Project Attachment Types">
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>
                                {dictionary.projectAttachmentTypeContentHeader || "Project Attachment Types"}
                            </Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        <Table.Row>
                            <Table.Cell>
                                <div className={styles.description}>
                                    {dictionary.attachmentTypeGuidance || "Users will select an attachment type when uploading a file to Sponsored Projects, Types that should not be available to users may be toggled off."}
                                </div>

                                <div className={styles.resolutionTable}>
                                    <Table>
                                        <Table.Header>
                                            <Table.Row>
                                                <Table.HeaderCell width="six">
                                                    {dictionary.titleColHeader || "Attachment Type Title"}
                                                </Table.HeaderCell>
                                                <Table.HeaderCell width="six">
                                                    {dictionary.activeColHeader || "Active Status"}
                                                </Table.HeaderCell>
                                            </Table.Row>
                                        </Table.Header>
                                        <Table.Body>{attachmentTypeRows}</Table.Body>
                                    </Table>
                                </div>

                                {addAttachmentTypeModal}
                            </Table.Cell>
                        </Table.Row>
                    </Table.Body>
                </Table>
            </BasePadding>
        </VerticalSpacing>
    );
};

export default ProjectAttachmentTypesSection;
